import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BookOpenIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { usePlaybookSchemes } from '../hooks/usePlaybookSchemes';
import { usePlaybookLibrary } from '../hooks/usePlaybookLibrary';
import PlaybookHandbook from './handbook/PlaybookHandbook';
import { PLAYBOOK_KEYFRAMES } from '../types/playbook.types';
import type { PlaybookScheme, PlaybookTaskType, PlaybookEntity } from '../types/playbook.types';

const SchemeManagerPanel: React.FC = () => {
  const { schemes, taskTypes, loading, error, loadSchemes, createScheme, updateMapping } = usePlaybookSchemes();
  const { playbooks, load: loadPlaybooks } = usePlaybookLibrary();
  const [selectedSchemeId, setSelectedSchemeId] = useState<string | null>(null);
  const [showHandbook, setShowHandbook] = useState(false);
  const [practiceFilter, setPracticeFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newSchemeName, setNewSchemeName] = useState('');
  const [newSchemePA, setNewSchemePA] = useState('');
  const [expandedPlaybookId, setExpandedPlaybookId] = useState<string | null>(null);

  useEffect(() => {
    loadSchemes(practiceFilter || undefined);
    loadPlaybooks({ lifecycle: 'active' });
  }, [loadSchemes, loadPlaybooks, practiceFilter]);

  const selectedScheme = schemes.find((s) => s.id === selectedSchemeId);

  const handleCreateScheme = useCallback(async () => {
    if (!newSchemeName.trim()) return;
    try {
      const created = await createScheme({
        name: newSchemeName,
        practice_area: newSchemePA || undefined,
      });
      setShowCreate(false);
      setNewSchemeName('');
      setNewSchemePA('');
      setSelectedSchemeId(created.id);
      loadSchemes(practiceFilter || undefined);
    } catch (err) {
      console.error('[SchemeManager] Create failed:', err);
    }
  }, [newSchemeName, newSchemePA, createScheme, loadSchemes, practiceFilter]);

  const handleMappingChange = useCallback(async (taskTypeId: string, playbookId: string) => {
    if (!selectedSchemeId) return;
    try {
      await updateMapping(selectedSchemeId, taskTypeId, playbookId);
      loadSchemes(practiceFilter || undefined);
    } catch (err) {
      console.error('[SchemeManager] Mapping failed:', err);
    }
  }, [selectedSchemeId, updateMapping, loadSchemes, practiceFilter]);

  // Active playbooks for dropdowns
  const activePlaybooks = playbooks.filter((p: PlaybookEntity) => p.lifecycle === 'active');

  // Build mapping lookup
  const mappingByTaskType = useMemo(() => {
    const map = new Map<string, string>();
    if (selectedScheme?.mappings) {
      for (const m of selectedScheme.mappings) {
        map.set(m.task_type_id, m.playbook_id);
      }
    }
    return map;
  }, [selectedScheme]);

  // Coverage summary
  const coverageSummary = useMemo(() => {
    if (!taskTypes.length) return null;
    const assigned = taskTypes.filter((tt) => mappingByTaskType.has(tt.id)).length;
    const total = taskTypes.length;
    const pct = Math.round((assigned / total) * 100);
    return { assigned, total, pct };
  }, [taskTypes, mappingByTaskType]);

  // Playbook lookup for preview
  const playbookById = useMemo(() => {
    const map = new Map<string, PlaybookEntity>();
    for (const pb of activePlaybooks) {
      map.set(pb.id, pb);
    }
    return map;
  }, [activePlaybooks]);

  return (
    <>
      <style>{PLAYBOOK_KEYFRAMES}</style>
      <div className="space-y-4" style={{ animation: 'fadeSlideIn 300ms ease-out' }}>
        {/* Header — Enhancement #1: Rename to Workflow Assignments */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Workflow Assignments</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Control which playbook runs automatically for each type of work.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowHandbook(true)}
            className="flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
          >
            <BookOpenIcon className="w-3.5 h-3.5" />
            Handbook
          </button>
        </div>

        {/* Filter + Actions — Enhancement #5: "Create My Version" replaces "New Scheme" */}
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={practiceFilter}
            onChange={(e) => setPracticeFilter(e.target.value)}
            placeholder="Filter by practice area..."
            className="rounded-md border border-gray-300 px-3 py-1.5 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 w-48"
          />
          <button
            type="button"
            onClick={() => setShowCreate(!showCreate)}
            className="rounded-md bg-lex-accent px-3 py-1.5 text-xs font-medium text-lex-accent-contrast hover:bg-lex-accent-hover"
          >
            + Create My Version
          </button>
        </div>

        {/* Create form */}
        {showCreate && (
          <div className="rounded-lg border border-lex-accent bg-lex-accent-soft p-3 space-y-2" style={{ animation: 'coverageDrillDown 200ms ease-out' }}>
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
              Create your own assignment set to customize which playbooks run for your firm.
            </p>
            <input
              type="text"
              value={newSchemeName}
              onChange={(e) => setNewSchemeName(e.target.value)}
              placeholder="Name (e.g., 'Family Law — My Firm')"
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-xs dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
            />
            <input
              type="text"
              value={newSchemePA}
              onChange={(e) => setNewSchemePA(e.target.value)}
              placeholder="Practice area (optional, e.g., 'Family Law')"
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-xs dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
            />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowCreate(false)}
                className="rounded-md border border-gray-300 px-3 py-1 text-xs text-gray-600 dark:border-gray-600 dark:text-gray-400">
                Cancel
              </button>
              <button type="button" onClick={handleCreateScheme}
                className="rounded-md bg-lex-accent px-3 py-1 text-xs font-medium text-lex-accent-contrast hover:bg-lex-accent-hover">
                Create
              </button>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center py-4">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-lex-accent border-t-transparent" />
            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">Loading...</span>
          </div>
        )}

        {error && (
          <div className="text-sm text-rose-600 dark:text-rose-400">{error}</div>
        )}

        {/* Enhancement #4: Scheme selector — fix double Default, make active prominent */}
        {!loading && schemes.length > 0 && (
          <div className="space-y-1.5">
            <label className="text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Assignment Sets
            </label>
            <div className="flex flex-wrap gap-2">
              {schemes.map((s) => {
                const isSelected = selectedSchemeId === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setSelectedSchemeId(s.id)}
                    className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                      isSelected
                        ? 'bg-lex-accent text-lex-accent-contrast ring-2 ring-lex-accent'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {s.name}
                    {s.is_system && !s.is_default && (
                      <span className="ml-1 text-[9px] opacity-70">(Built-in)</span>
                    )}
                    {s.is_default && (
                      <span className={`ml-1.5 inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${
                        isSelected
                          ? 'bg-white/20 text-white'
                          : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                      }`}>
                        Active
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Mapping grid — Enhancements #2, #3, #5, #6, #7 */}
        {selectedScheme && (
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            {/* Scheme header */}
            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{selectedScheme.name}</h3>
                  {selectedScheme.practice_area && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">{selectedScheme.practice_area}</span>
                  )}
                </div>
                {/* Enhancement #5: System scheme notice */}
                {selectedScheme.is_system && (
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-amber-600 dark:text-amber-400">
                      Built-in — read only
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setNewSchemeName(`${selectedScheme.name} (Custom)`);
                        setNewSchemePA(selectedScheme.practice_area || '');
                        setShowCreate(true);
                      }}
                      className="rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1 text-[10px] font-medium text-amber-700 hover:bg-amber-100 dark:border-amber-600 dark:bg-amber-900/20 dark:text-amber-300 dark:hover:bg-amber-900/40"
                    >
                      Create My Version
                    </button>
                  </div>
                )}
              </div>

              {/* Enhancement #6: Coverage summary */}
              {coverageSummary && (
                <div className="mt-2">
                  <div className="flex items-center justify-between text-[10px] mb-1">
                    <span className="text-gray-500 dark:text-gray-400">
                      {coverageSummary.assigned} of {coverageSummary.total} task types have a playbook assigned
                    </span>
                    <span className={`font-semibold ${
                      coverageSummary.pct === 100
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : coverageSummary.pct >= 50
                          ? 'text-amber-600 dark:text-amber-400'
                          : 'text-rose-600 dark:text-rose-400'
                    }`}>
                      {coverageSummary.pct}%
                    </span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${
                        coverageSummary.pct === 100
                          ? 'bg-emerald-500'
                          : coverageSummary.pct >= 50
                            ? 'bg-amber-500'
                            : 'bg-rose-500'
                      }`}
                      style={{ width: `${coverageSummary.pct}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Task type mapping rows */}
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {taskTypes.length === 0 ? (
                <div className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">No task types defined yet.</div>
              ) : (
                taskTypes.map((tt) => {
                  const assignedPbId = mappingByTaskType.get(tt.id) || '';
                  const assignedPb = assignedPbId ? playbookById.get(assignedPbId) : null;
                  const isExpanded = expandedPlaybookId === tt.id;

                  return (
                    <div key={tt.id} className="px-4 py-3">
                      {/* Enhancement #2: Cause-and-effect row layout */}
                      {/* Enhancement #3: Full task type name, no truncation */}
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mb-1">
                            <span className="font-medium text-gray-700 dark:text-gray-300">When</span>
                            <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-medium ${
                              tt.category === 'subtask'
                                ? 'bg-violet-100 text-violet-600 dark:bg-violet-900/30 dark:text-violet-300'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                            }`}>
                              {tt.category}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            {tt.icon && <span className="text-sm">{tt.icon}</span>}
                            <span className="text-sm font-medium text-gray-900 dark:text-white">{tt.name}</span>
                          </div>
                          {tt.description && (
                            <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">{tt.description}</p>
                          )}
                        </div>

                        <div className="flex items-center gap-2 shrink-0 pt-4">
                          <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
                            run
                          </span>
                          <svg className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        </div>

                        <div className="w-56 shrink-0 pt-3">
                          <select
                            value={assignedPbId}
                            onChange={(e) => handleMappingChange(tt.id, e.target.value)}
                            disabled={selectedScheme.is_system}
                            className={`w-full rounded-md border px-2 py-1.5 text-xs transition-colors disabled:opacity-50 ${
                              assignedPbId
                                ? 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-200'
                                : 'border-gray-300 text-gray-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400'
                            }`}
                          >
                            <option value="">No playbook</option>
                            {activePlaybooks.map((pb: PlaybookEntity) => (
                              <option key={pb.id} value={pb.id}>{pb.name} (v{pb.version})</option>
                            ))}
                          </select>
                        </div>

                        {/* Enhancement #7: Playbook preview toggle */}
                        {assignedPb && (
                          <button
                            type="button"
                            onClick={() => setExpandedPlaybookId(isExpanded ? null : tt.id)}
                            className="shrink-0 p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 mt-3"
                            title="Preview playbook"
                          >
                            {isExpanded ? (
                              <ChevronUpIcon className="w-4 h-4" />
                            ) : (
                              <ChevronDownIcon className="w-4 h-4" />
                            )}
                          </button>
                        )}
                      </div>

                      {/* Enhancement #7: Playbook preview panel */}
                      {isExpanded && assignedPb && (
                        <div
                          className="mt-2 ml-0 rounded-md border border-lex-accent bg-lex-accent-soft px-3 py-2"
                          style={{ animation: 'coverageDrillDown 200ms ease-out' }}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="text-xs font-semibold text-gray-900 dark:text-white">
                                {assignedPb.name}
                              </div>
                              {assignedPb.description && (
                                <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 max-w-md">
                                  {assignedPb.description}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-[10px] text-gray-400 dark:text-gray-500">
                              <span>v{assignedPb.version}</span>
                              <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-medium ${
                                assignedPb.lifecycle === 'active'
                                  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                              }`}>
                                {assignedPb.lifecycle}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 mt-1.5 text-[10px] text-gray-500 dark:text-gray-400">
                            <span>{assignedPb.status_count ?? assignedPb.statuses?.length ?? '?'} statuses</span>
                            <span>{assignedPb.transition_count ?? assignedPb.transitions?.length ?? '?'} transitions</span>
                            {assignedPb.practice_areas?.length > 0 && (
                              <span>{assignedPb.practice_areas.join(', ')}</span>
                            )}
                          </div>
                          {/* Mini flow preview: initial → ... → terminal */}
                          {assignedPb.statuses && assignedPb.statuses.length > 0 && (
                            <div className="flex items-center gap-1 mt-2 flex-wrap">
                              {assignedPb.statuses
                                .filter((s) => s.is_initial)
                                .map((s) => (
                                  <span key={s.id} className="inline-flex items-center rounded-full bg-lex-accent-soft px-2 py-0.5 text-[9px] font-medium text-lex-accent">
                                    {s.name}
                                  </span>
                                ))}
                              {assignedPb.statuses.some((s) => s.is_initial) && assignedPb.statuses.some((s) => s.is_terminal) && (
                                <svg className="w-3 h-3 text-gray-300 dark:text-gray-600 mx-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                </svg>
                              )}
                              {assignedPb.statuses
                                .filter((s) => !s.is_initial && !s.is_terminal)
                                .slice(0, 3)
                                .map((s) => (
                                  <span key={s.id} className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[9px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                                    {s.name}
                                  </span>
                                ))}
                              {assignedPb.statuses.filter((s) => !s.is_initial && !s.is_terminal).length > 3 && (
                                <span className="text-[9px] text-gray-400 dark:text-gray-500">
                                  +{assignedPb.statuses.filter((s) => !s.is_initial && !s.is_terminal).length - 3} more
                                </span>
                              )}
                              {assignedPb.statuses.some((s) => s.is_terminal) && (
                                <>
                                  <svg className="w-3 h-3 text-gray-300 dark:text-gray-600 mx-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                  </svg>
                                  {assignedPb.statuses
                                    .filter((s) => s.is_terminal)
                                    .map((s) => (
                                      <span key={s.id} className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[9px] font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                                        {s.name}
                                      </span>
                                    ))}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Empty state */}
        {!loading && schemes.length === 0 && (
          <div className="text-center py-12">
            <p className="text-sm text-gray-500 dark:text-gray-400">No workflow assignments found.</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Create an assignment set to control which playbooks run for each task type.
            </p>
          </div>
        )}
      </div>

      {showHandbook && (
        <PlaybookHandbook
          isOpen={showHandbook}
          onClose={() => setShowHandbook(false)}
          initialChapter="schemes"
        />
      )}
    </>
  );
};

export default SchemeManagerPanel;
